import moment from 'moment';
import {
  DATE_TIME_FORMAT_LONG,
  UNIT_LENGTH,
  UNIT_ROUNDING_THRESHOLD,
} from 'modules/documentation/constants';
import {formatDate, isToday} from '../NoteForm';
import type {Choice} from './choices';
import {
  CHANGE_STATUSES,
  CONTACT_METHODS,
  FOLLOW_UP_WITH_CLIENT,
  FOLLOW_UPS,
  NOT_REACHED,
  NOT_REACHED_REASONS,
  NO_DIRECT_CONTACT,
  OTHER,
  OTHER_PLACE_OF_SERVICE,
  REASONS,
  RESPONSES,
  STATUS_NEEDS,
  SERVICE_ACTIVITIES,
  labelOf,
} from './choices';
import type {Checklist, ContactNoteState} from './state';

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const durationMinutes = (start: string, end: string): number | null => {
  if (!start || !end) {
    return null;
  }
  const minutes = toMinutes(end) - toMinutes(start);
  return minutes > 0 ? minutes : null;
};

export const billableUnits = (minutes: number) =>
  Math.floor(minutes / UNIT_LENGTH) +
  (minutes % UNIT_LENGTH >= UNIT_ROUNDING_THRESHOLD ? 1 : 0);

// Only today is checked, because the date field already flags future dates
export const isEndInFuture = ({
  dateOfService,
  endTime,
}: Pick<ContactNoteState, 'dateOfService' | 'endTime'>) =>
  isToday(dateOfService) &&
  toMinutes(endTime) > toMinutes(moment().format('HH:mm'));

export const unitsLabel = (units: number) =>
  `${units} ${units === 1 ? 'unit' : 'units'}`;

export const needsClinicalDocumentation = ({
  serviceCode,
  contactMethod,
}: Pick<ContactNoteState, 'serviceCode' | 'contactMethod'>) =>
  serviceCode === 'H0023' &&
  (contactMethod === 'secure_message_text' || contactMethod === 'email_other');

const lowerFirst = (text: string) =>
  text.charAt(0).toLowerCase() + text.slice(1);

const joinList = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const otherOr = (key: string | null, other: string, list: readonly Choice[]) =>
  key === OTHER ? other.trim() || 'other' : lowerFirst(labelOf(list, key));

const checklistLabels = (
  checklist: Checklist,
  list: readonly Choice[],
  describe: (key: string, label: string) => string = (_, label) => label,
) =>
  list
    .filter(({key}) => checklist.selected.includes(key))
    .map(({key}) => describe(key, otherOr(key, checklist.other, list)));

// Typed line breaks are flattened so only section breaks separate paragraphs
const paragraph = (...parts: string[]) =>
  parts
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((part) => (/[.!?]$/.test(part) ? part : `${part}.`))
    .join(' ');

const paragraphs = (...sections: string[]) =>
  sections.filter(Boolean).join('\n\n');

const hasNoDirectContact = (state: ContactNoteState) =>
  state.responses.selected.includes(NO_DIRECT_CONTACT);

// With Other, the details are the method's name, so they aren't repeated in brackets
const contactDetailOf = (state: ContactNoteState) => {
  const detail = state.contactMethodDetail.trim();
  return detail && state.contactMethod !== OTHER ? ` (${detail})` : '';
};

const composeNotReached = (state: ContactNoteState) => {
  const reason = state.notReachedReason
    ? otherOr(
        state.notReachedReason,
        state.notReachedReasonOther,
        NOT_REACHED_REASONS,
      )
    : '';
  return paragraphs(
    paragraph(
      `Attempted to reach the member${contactDetailOf(state)}${
        reason ? ` regarding ${reason}` : ''
      }; member was not reached`,
      state.notReachedNote,
    ),
    paragraph(
      state.nextAttemptAt
        ? `Next attempt scheduled ${formatDate(state.nextAttemptAt, DATE_TIME_FORMAT_LONG)}`
        : '',
    ),
  );
};

const composeContact = (state: ContactNoteState) => {
  const method = state.contactMethod
    ? otherOr(state.contactMethod, state.contactMethodDetail, CONTACT_METHODS)
    : '';
  const reasons = joinList(checklistLabels(state.reasons, REASONS));
  if (!method && !reasons) {
    return '';
  }
  // Staff can record outreach that never reached the member, so don't claim contact
  const opening = hasNoDirectContact(state)
    ? 'Outreach'
    : 'Contacted the member';
  return `${opening}${method ? ` by ${method}${contactDetailOf(state)}` : ''}${
    reasons ? ` regarding ${reasons}` : ''
  }`;
};

const composeStatus = (state: ContactNoteState) => {
  const status = state.changeStatus
    ? lowerFirst(labelOf(CHANGE_STATUSES, state.changeStatus))
    : '';
  const needs = joinList(checklistLabels(state.needs, STATUS_NEEDS));
  if (!status && !needs) {
    return '';
  }
  return `Change since last note: ${[status, needs && `(${needs})`]
    .filter(Boolean)
    .join(' ')}`;
};

const composeService = (state: ContactNoteState) => {
  const activities = joinList(
    checklistLabels(state.activities, SERVICE_ACTIVITIES),
  );
  const specificNeed = state.specificNeed.trim();
  const pick =
    state.placeOfService?.confirmedFor === state.dateOfService
      ? state.placeOfService
      : null;
  const place =
    pick?.code === OTHER_PLACE_OF_SERVICE
      ? state.placeOfServiceOther.trim()
      : pick?.label;
  return [
    activities && `Service provided: ${activities}`,
    place ? `Place of service: ${place}` : '',
    specificNeed && `Specific need addressed: ${specificNeed}`,
  ];
};

const composeBilling = (state: ContactNoteState) => {
  if (!state.serviceCode) {
    return '';
  }
  const minutes = durationMinutes(state.startTime, state.endTime);
  return minutes === null
    ? `Service code: ${state.serviceCode}`
    : `Service code: ${state.serviceCode}, ${minutes} minutes (${state.startTime}-${state.endTime}) ${unitsLabel(billableUnits(minutes))}`;
};

const composeFollowUp = (state: ContactNoteState) =>
  joinList(
    checklistLabels(state.followUps, FOLLOW_UPS, (key, label) =>
      key === FOLLOW_UP_WITH_CLIENT && state.followUpAt
        ? `${label} on ${formatDate(state.followUpAt, DATE_TIME_FORMAT_LONG)}`
        : label,
    ),
  );

// A fact about the outreach, not the member's response, so it goes in Data
const composeNoDirectContact = (state: ContactNoteState) =>
  hasNoDirectContact(state) ? labelOf(RESPONSES, NO_DIRECT_CONTACT) : '';

const composeResponse = (state: ContactNoteState) => {
  if (hasNoDirectContact(state)) {
    return '';
  }
  const responses = joinList(checklistLabels(state.responses, RESPONSES));
  return responses && `Member response: ${responses}`;
};

const composeReached = (state: ContactNoteState) => {
  const data = paragraph(
    composeContact(state),
    composeNoDirectContact(state),
    state.objectiveReason,
    ...composeService(state),
    state.serviceDescription,
    composeBilling(state),
  );
  const assessment = paragraph(
    composeStatus(state),
    state.relevantChange,
    state.observation,
    composeResponse(state),
    state.responseOutcome,
  );
  const followUps = composeFollowUp(state);
  const plan = paragraph(
    followUps && `Follow-up: ${followUps}`,
    state.followUpPlan,
  );

  return paragraphs(data, assessment, plan);
};

const SOURCE_LINE = 'Narrative generated in Pathfinder Bridge.';

export const composeNarrative = (state: ContactNoteState): string => {
  const body =
    state.contactMethod === NOT_REACHED
      ? composeNotReached(state)
      : composeReached(state);
  // Only with content, so a blank note still reads as missing its narrative
  return body && paragraphs(body, SOURCE_LINE);
};

export const narrativeOf = (state: ContactNoteState) =>
  state.narrative ?? composeNarrative(state);
